import { find } from "lodash";
import { useMemo } from "react";
import moment from "moment";

import { BULK_ITEM_CATEGORY_OTHER_ID, DEFAULT_TIME } from "src/constants";
import { BulkyItem } from "src/interfaces";
import { getItemSettings } from "src/utils/bulkyItems";
import {
  PickupScheduleSummaryCard,
  PickupScheduleSummaryContent,
  PickupScheduleSummaryLeftContent,
  PickupScheduleSummaryRightMiddleValue,
  PickupScheduleSummaryRightValue,
  TextDisplay,
} from "src/styled";
import { translate } from "src/services";
import { UnconnectedCheckbox } from "src/components";
import { useAppSelector } from "src/redux/store";

interface Props {
  checkboxValue: boolean;
  isCancelling: boolean;
  item: BulkyItem;
  onCheckboxChange: (value: boolean) => void;
}

const ItemSummaryCard = ({ checkboxValue, isCancelling, item, onCheckboxChange }: Props) => {
  const vendorSettings = useAppSelector((state) => state.citySettings.settings?.vendorSettings);
  const categorySettings = useAppSelector((state) => state.citySettings.settings?.categories);
  const addressValidation = useAppSelector((state) => state.bulkyItemsScheduler.addressValidation);

  const categorySetting = find(categorySettings, {
    bulkyCategoryTypeId: item.bulkyCategoryTypeId,
  });

  const itemSetting =
    item.bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID
      ? find(categorySetting?.items, {
          id: item.bulkyItemTypeId,
        })
      : find(categorySetting?.items, {
          bulkyItemTypeId: item.bulkyItemTypeId,
        });

  const noteToDisplay = useMemo(() => {
    return itemSetting?.message || categorySetting?.message || "";
  }, [categorySetting?.message, itemSetting?.message]);

  const itemCustomSettings = useMemo(() => {
    if (!categorySettings || !vendorSettings) return null;
    return getItemSettings(item, item.pickupDate, categorySettings, addressValidation);
  }, [categorySettings, item, vendorSettings, addressValidation]);

  if (!categorySetting) return null;

  return (
    <PickupScheduleSummaryCard hasRightValues={true} isCanceled={item.isCancelled}>
      <PickupScheduleSummaryLeftContent
        flexDirection="column"
        isBigger
        hasPrice={
          !!item.pickupDate && (!!itemCustomSettings?.price || itemCustomSettings?.price === 0)
        }
      >
        <TextDisplay size="medium">
          <b> {item.quantity?.toString() || ""}</b>
        </TextDisplay>

        {!!item.pickupDate && (!!itemCustomSettings?.price || itemCustomSettings?.price === 0) && (
          <TextDisplay size="small" hasBackground>
            <b>{`$${itemCustomSettings?.price}` || ""}</b>
          </TextDisplay>
        )}
      </PickupScheduleSummaryLeftContent>

      <PickupScheduleSummaryRightValue hasCheckBox={isCancelling && !item.isCancelled}>
        <TextDisplay color="grayDarker" size="small">
          <b>{item.pickupDate ? moment(item.pickupDate).format("MM/DD/YYYY") : ""}</b>
        </TextDisplay>
      </PickupScheduleSummaryRightValue>

      {isCancelling && onCheckboxChange && !item.isCancelled && (
        <PickupScheduleSummaryRightValue key="rightValue">
          <UnconnectedCheckbox
            onChange={(ev) => onCheckboxChange(ev.target.checked)}
            checked={checkboxValue || false}
          />
        </PickupScheduleSummaryRightValue>
      )}
      <PickupScheduleSummaryRightMiddleValue>
        <TextDisplay color="grayDarker" size="xSmall">
          {(itemCustomSettings?.pickupWindowStartTime !== DEFAULT_TIME ||
            itemCustomSettings?.pickupWindowEndTime !== DEFAULT_TIME) &&
            item.pickupDate && (
              <b>
                {moment(itemCustomSettings?.pickupWindowStartTime, "HH:mm:ss").format("hh:mm A")} -{" "}
                {moment(itemCustomSettings?.pickupWindowEndTime, "HH:mm:ss").format("hh:mm A")}
              </b>
            )}
        </TextDisplay>
      </PickupScheduleSummaryRightMiddleValue>
      <PickupScheduleSummaryContent>
        <TextDisplay color="grayDarker" size="small" margin="no no xxSmall no">
          {item.categoryLabelToDisplay || ""}{" "}
          {item.isCancelled && (
            <TextDisplay color="alert" size="small" uppercase>
              {translate("common.cancelled")}
            </TextDisplay>
          )}
        </TextDisplay>
        <TextDisplay size="small">
          <b>{item.itemLabelToDisplay || item.customBulkyItemName || ""}</b>
        </TextDisplay>
        <TextDisplay size="xSmall" margin="xxSmall no no no">
          {noteToDisplay}

          {item.note && (
            <>
              <br /> {translate("common.note")}: {item.note}
            </>
          )}
        </TextDisplay>
      </PickupScheduleSummaryContent>
    </PickupScheduleSummaryCard>
  );
};

export default ItemSummaryCard;
